import {Component, ElementRef, HostListener, OnDestroy, ViewChild} from '@angular/core';
import {ICellEditorParams} from 'ag-grid-community';
import {IDepartment} from '../../../../shared/shared-models/stock/departments';
import {Store} from '@ngrx/store';
import {StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {selectSelectedStoreStockTags} from '../../../../features/settings-new/store/setting.selectors';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {getDeepCopyOfObject} from '../../../../shared/shared-utils/object/object.utils';
import {normalizeStockItemTagValues} from '../../../../features/stock-manager/utils/stock-manager-barcode.utils';

@Component({
  selector: 'app-cell-editor-multi-select-array',
  templateUrl: './cell-editor-multi-select-array.component.html',
  styleUrls: ['./cell-editor-multi-select-array.component.scss'],
  standalone: false,
})
export class CellEditorMultiSelectArrayComponent implements ICellEditorAngularComp, OnDestroy {

  @ViewChild('input', {static: true}) input: ElementRef;
  @ViewChild('selectElement', {static: true}) selectElement: ElementRef<HTMLSelectElement>;
  tags = ['in_ngp', 'ao_flag', 'onHold', 'test_tag'];
  value = '';
  params: ICellEditorParams;
  width: number;
  colDefField: string;
  icons: typeof IconsEnum = IconsEnum;
  departments: IDepartment[] = [];
  displayAbove = false;
  selectedValues: string[] = [];
  showOptions = false;
  protected readonly object = Object;
  private destroy$ = new Subject<boolean>();
  private rowData: StockItem | NGPReport;
  /** Tags committed in the cell text when the options dropdown was opened. */
  private dropdownSnapshotTags: string[] | null = null;

  constructor(
    private readonly store: Store,
  ) {
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.showOptions) {
      this.adjustPosition();
    }
  }

  agInit(params: ICellEditorParams): void {
    this.store.select(selectSelectedStoreStockTags).pipe(takeUntil(this.destroy$)).subscribe((tags: {
      name: string,
      global: boolean
    }[]) => {
      this.tags = tags.map((tag: { name: string, global: boolean }) => tag.name);
    });
    this.params = {...params};
    this.rowData = getDeepCopyOfObject(params.data);
    this.colDefField = params.colDef.field;
    const initialTags = normalizeStockItemTagValues(params.value ?? this.params.data[this.colDefField]);
    this.selectedValues = initialTags;
    this.value = initialTags.join(',');
    this.width = this.params.colDef.width;
    params.api.setColumnWidths([
      {key: params.colDef.field, newWidth: 200},
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getValue(): string[] {
    const newValues = normalizeStockItemTagValues(this.value);
    this.selectedValues = newValues;
    this.value = newValues.join(',');
    this.rowData[this.colDefField] = newValues;

    const originalValues = this.params?.data?.originalValue?.[this.colDefField]?.value as string[];
    this.rowData.isEdited = !this.arraysAreEqual(newValues, originalValues);

    GridUtils.updateSingleItem(
      this.rowData,
      this.colDefField,
      newValues,
      this.store,
      'stock-manager',
    );
    GridUtils.updateIsEditedAndForceRefresh({...this.params, data: this.rowData});

    return newValues;
  }


  arraysAreEqual(arr1: string[], arr2: string[]): boolean {
    if (!arr1 || !arr2) {
      return false;
    }
    if (arr1.length !== arr2.length) {
      return false;
    }
    return arr1.every((value, index) => value === arr2[index]);
  }

  /**
   * True when the current tag selection differs from the row original (value compare, not reference).
   */
  hasUnsavedTagChanges(): boolean {
    const originalTags = normalizeStockItemTagValues(
      this.params?.data?.originalValue?.[this.colDefField]?.value,
    );
    const currentTags = normalizeStockItemTagValues(this.value);
    return !this.arraysAreEqual(originalTags, currentTags);
  }

  refresh(params: ICellEditorParams<NGPReport | StockItem, string | number>): boolean {
    GridUtils.updateIsEditedAndForceRefresh(this.params);
    return false;
  }

  afterGuiAttached(): void {
    this.input.nativeElement.focus();
  }

  onUndoChangesClick(): void {
    const originalTags = normalizeStockItemTagValues(this.params.data.originalValue[this.colDefField].value);
    this.value = originalTags.join(',');
    this.selectedValues = [...originalTags];
    this.rowData[this.colDefField] = [...originalTags];
    this.dropdownSnapshotTags = null;
    this.showOptions = false;
    GridUtils.removeIsEditingFlagAndDispatch(this.rowData, this.colDefField, this.store, 'stock-manager');
    GridUtils.updateIsEditedAndForceRefresh(this.params);
  }

  toggleDropdown(): void {
    if (this.showOptions) {
      this.cancelDropdownEdits();
      this.showOptions = false;
      return;
    }
    this.openDropdown();
  }

  adjustPosition(): void {
    const inputRect = this.input.nativeElement.getBoundingClientRect();
    const optionsHeight = 400;
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    this.displayAbove = spaceBelow < optionsHeight && spaceAbove > optionsHeight;
  }

  selectText(): void {
    setTimeout((): void => {
      this.input.nativeElement.focus();
      this.input.nativeElement.setSelectionRange(0, this.value?.toString()?.length);
      this.input.nativeElement.select();
    }, 1);
  }

  toggleSelection(value: string): void {
    if (!Array.isArray(this.selectedValues)) {
      this.selectedValues = [];
    }

    const index = this.selectedValues.indexOf(value);

    if (index !== -1) {
      this.selectedValues = this.selectedValues?.filter((v: string) => v !== value);
    } else {
      this.selectedValues = [...this.selectedValues, value];
    }

    this.selectedValues = this.selectedValues?.filter((item: string) => item !== '');
  }

  onCancelButtonClick(): void {
    this.cancelDropdownEdits();
    this.showOptions = false;
    this.selectText();
  }

  onConfirmButtonClick(): void {
    const committedTags = normalizeStockItemTagValues(this.selectedValues);
    this.value = committedTags.join(',');
    this.selectedValues = committedTags;
    this.rowData[this.colDefField] = committedTags;
    this.params.value = committedTags;
    this.dropdownSnapshotTags = null;
    this.showOptions = false;
    this.selectText();
  }

  private openDropdown(): void {
    const committedTags = normalizeStockItemTagValues(this.value);
    this.dropdownSnapshotTags = [...committedTags];
    this.selectedValues = [...committedTags];
    this.showOptions = true;
    this.adjustPosition();
  }

  private cancelDropdownEdits(): void {
    if (!this.dropdownSnapshotTags) {
      return;
    }
    this.selectedValues = [...this.dropdownSnapshotTags];
    this.value = this.dropdownSnapshotTags.join(',');
    this.rowData[this.colDefField] = [...this.dropdownSnapshotTags];
    this.dropdownSnapshotTags = null;
  }
}
